/**
 * Sidebar Component
 * Main navigation sidebar for the workspace application.
 *
 * Ported from v0.26 legacy app -- adapted for verisa_v1 project structure.
 *
 * Features:
 * - The installation logo and product name at top (from the BrandingProvider)
 * - Search bar (placeholder for MVP)
 * - Navigation sections: Agents, Report History
 * - LocalePicker + user info at bottom
 * - Responsive: collapsible on mobile
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import LogoutIcon from '@mui/icons-material/Logout';
import { useLocation } from 'react-router-dom';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { colors } from '../../theme/colors';
import NavItem from './NavItem';
import type { NavItemData } from './NavItem';
import { useAuth } from '../../store/AuthContext';
import { LocalePicker } from '../shared/LocalePicker';
import { getAgent } from '../../api/workspace';
import { AccessibilityDrawer } from '../accessibility';
import { FeatureGate } from '../../feature-gate';
import { useBranding } from '../../branding/BrandingProvider';
import { DefaultBranding } from '../../branding/defaults';

/** Sidebar width when expanded */
export const SIDEBAR_WIDTH = 240;
/** Sidebar width when collapsed (tablet mode) */
export const SIDEBAR_COLLAPSED_WIDTH = 56;

interface SidebarProps {
  /** Whether the mobile drawer is open */
  mobileOpen?: boolean;
  /** Handler to toggle mobile drawer */
  onMobileToggle?: () => void;
}

/**
 * Dashboard icon SVG
 */
const DashboardIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);

/**
 * The installation brand at the top of the sidebar -- image + text label.
 *
 * BOTH VALUES COME FROM THE ONE BRANDING READER mounted at the SPA root (UI-004,
 * `apps/workspace/App.tsx`): `logo_url` is the image source and `product_name` is
 * BOTH the image's accessible name and the text label beside it. This component
 * fetches nothing (SPEC-FRONTEND-RULE-077) and holds no product name of its own
 * (RULE-012); the app suffix under the name stays a Lingui string (D-BR-08), which
 * is why `<Trans>Workspace</Trans>` below is untouched.
 *
 * FAIL-OPEN, AND IT IS THREE POSTURES, NOT ONE (RULE-001, D-BR-15). The provider
 * hands back `DefaultBranding` both while the bounded read is in flight
 * (`loading`) and after it failed (`unavailable`), so those two render the
 * built-in logo and the built-in name silently, with no console line and no
 * rendered error. The THIRD posture is a record that WAS read and carries no logo
 * (`logo_url: null`, CONTRACT-001), which the context cannot cover for us: that is
 * what the `defaults.ts` fallback below is for. A `null` src would render a broken
 * image and a dropped image would leave an empty sidebar header - the RULE-042
 * degraded state a cosmetic failure must never reach.
 *
 * `web/src/branding/defaults.ts` is the ONE file allowed to hold the vendor
 * literal (RULE-012), so the built-in path is IMPORTED here and never retyped.
 *
 * The name, the collapsed / expanded layout, the sizes and objectFit are
 * deliberately unchanged: only the two values moved.
 *
 * @see SPEC-PLATFORM-BRANDING-RULE-001 / RULE-012
 * @see SPEC-PLATFORM-BRANDING-UI-004
 * @see SPEC-PLATFORM-BRANDING-SCENARIO-005
 * @see TASK-PB-BRAND-WORKSPACE-SHELL-CHG-001
 */
const VerisaLogo = ({ collapsed }: { collapsed: boolean }) => {
  const { branding } = useBranding();
  // `Branding.logo_url` is nullable by CONTRACT-001, and so is the TYPE of the
  // built-in field although its value never is; the trailing `?? undefined` is
  // that type's tail rather than a third fallback - `src={null}` is not something
  // an <img> can carry.
  const logoSrc = branding.logo_url ?? DefaultBranding.logo_url ?? undefined;
  const productName = branding.product_name || DefaultBranding.product_name;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: collapsed ? 0 : 2,
        py: 2,
        justifyContent: 'center',
      }}
    >
      {/* The accessible name is the product name in BOTH states (RULE-046): the
          collapsed sidebar drops the text label, so this `alt` is the only name
          the header has there. */}
      <Box
        component="img"
        src={logoSrc}
        alt={productName}
        sx={{
          height: 40,
          width: 40,
          objectFit: 'contain',
          flexShrink: 0,
        }}
      />

      {!collapsed && (
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '16px',
              color: colors.text.primary,
              lineHeight: 1.2,
            }}
          >
            {productName}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: colors.text.secondary,
              fontSize: '11px',
            }}
          >
            <Trans>Workspace</Trans>
          </Typography>
        </Box>
      )}
    </Box>
  );
};

/**
 * Extract agentId from the current URL path if on an agent page.
 * Matches /agents/:agentId and /agents/:agentId/*
 */
const extractAgentId = (pathname: string): string | null => {
  const match = pathname.match(/^\/agents\/([^/]+)/);
  return match ? match[1] : null;
};

/** Global navigation items (always visible) */
const getGlobalNavItems = (label: string): NavItemData[] => [
  {
    text: label,
    icon: <DashboardIcon />,
    path: '/agents',
    matchPaths: ['/agents/'],
  },
];

/** Agent-specific navigation items (only when viewing an agent) */
const getAgentNavItems = (agentId: string, label: string): NavItemData[] => [
  {
    text: label,
    icon: <HistoryIcon />,
    path: `/agents/${agentId}/report-history`,
  },
];

/**
 * Sidebar renders the main navigation drawer.
 * Responsive behavior:
 * - Desktop (>= 960px): Permanent drawer, expanded
 * - Tablet (600-959px): Permanent drawer, collapsed (icons only)
 * - Mobile (< 600px): Temporary drawer, opens via hamburger menu
 */
const Sidebar: React.FC<SidebarProps> = ({
  mobileOpen = false,
  onMobileToggle,
}) => {
  const theme = useTheme();
  const location = useLocation();
  const { _: lingui } = useLingui();
  const { user, logout } = useAuth();
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const currentAgentId = extractAgentId(location.pathname);

  // Fetch agent name and plugin_id when viewing an agent page
  const [agentName, setAgentName] = useState<string | null>(null);
  const [agentPluginId, setAgentPluginId] = useState<string | null>(null);

  useEffect(() => {
    if (!currentAgentId) {
      setAgentName(null);
      setAgentPluginId(null);
      return;
    }
    let cancelled = false;
    getAgent(currentAgentId)
      .then((agent) => {
        if (cancelled) return;
        setAgentName(agent.name || null);
        setAgentPluginId(agent.plugin_id ?? null);
      })
      .catch((err) => {
        console.error("[Sidebar] Failed to load agent metadata:", err);
        if (!cancelled) {
          setAgentName(null);
          setAgentPluginId(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [currentAgentId]);

  // Determine if sidebar should be collapsed (tablet mode)
  const isCollapsed = isTablet;
  const currentWidth = isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  /**
   * Renders the drawer content.
   * @param collapsed - Whether to render in collapsed (icon-only) mode
   */
  const renderDrawerContent = (collapsed: boolean) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: colors.background.secondary,
      }}
    >
      {/* Logo Section */}
      <VerisaLogo collapsed={collapsed} />

      {/* Navigation Items */}
      <Box
        component="nav"
        sx={{
          flex: 1,
          px: collapsed ? 1 : 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
        aria-label={lingui(msg`Main navigation`)}
      >
        {/* Global nav items */}
        {getGlobalNavItems(lingui(msg`Agents`)).map((item) => (
          <NavItem key={item.path} item={item} collapsed={collapsed} />
        ))}

        {/* Divider always visible below global nav */}
        <Divider sx={{ my: 1, borderColor: colors.border.default }} />

        {/* Agent-specific section (agent name header + nav items) */}
        {currentAgentId && (
          <>
            {/* Agent name label */}
            {agentName && !collapsed && (
              <Box
                sx={{
                  mx: 0,
                  mb: 0.5,
                  px: 2.5,
                  py: 1,
                  borderRadius: 1.5,
                  bgcolor: colors.text.primary,
                  color: '#fff',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    lineHeight: 1.3,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {agentName}
                </Typography>
              </Box>
            )}
            {/*
              Report History is a SHIPPED feature (not an in-flight release
              toggle — it had no entry in the legacy visibility map, so it
              resolved visible). Under the fail-closed Feature Gate it carries
              no toggle and renders unconditionally for the doc-processor
              plugin. `agentPluginId === 'doc-processor'` is a plugin-kind
              condition, NOT a feature toggle — it is preserved.
            */}
            {agentPluginId === 'doc-processor' &&
              getAgentNavItems(currentAgentId, lingui(msg`Report History`)).map(
                (item) => (
                  <NavItem
                    key={item.path}
                    item={item}
                    collapsed={collapsed}
                  />
                ),
              )}
          </>
        )}
      </Box>

      {/* Bottom Section */}
      <Box sx={{ mt: 'auto' }}>
        {/* Locale Picker */}
        <Box
          sx={{
            px: collapsed ? 1 : 1.5,
            pb: 1,
            display: 'flex',
            justifyContent: collapsed ? 'center' : 'flex-start',
          }}
        >
          <LocalePicker />
        </Box>

        <Box
          sx={{
            px: collapsed ? 1 : 1.5,
            pb: 1,
            display: 'flex',
            justifyContent: collapsed ? 'center' : 'flex-start',
          }}
        >
          <FeatureGate feature="global.accessibility">
            <AccessibilityDrawer />
          </FeatureGate>
        </Box>

        <Divider sx={{ borderColor: colors.border.default }} />

        {/* User Info (inline) */}
        <Box
          sx={{
            p: collapsed ? 1 : 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            justifyContent: collapsed ? 'center' : 'space-between',
          }}
        >
          {!collapsed && (
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 500,
                color: colors.text.primary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
              }}
            >
              {user?.name ?? lingui(msg`User`)}
            </Typography>
          )}
          <IconButton
            size="small"
            onClick={logout}
            aria-label={lingui(msg`Log out`)}
            sx={{
              color: colors.text.secondary,
              '&:hover': { color: colors.text.primary },
            }}
          >
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile Drawer - Temporary */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileToggle}
        ModalProps={{
          keepMounted: true, // Better performance on mobile
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: SIDEBAR_WIDTH,
          },
        }}
      >
        {renderDrawerContent(false)}
      </Drawer>

      {/* Desktop/Tablet Drawer - Permanent.
          IN FLOW, NOT VIEWPORT-FIXED (SPEC-PLATFORM-BRANDING RULE-020, D-BR-29).
          MUI's drawer paper is `position: fixed; top: 0` by default, so this
          navigation ran to the bottom edge of the VIEWPORT and AppLayout had to
          offset its content with a `margin-left` to clear it. Both halves go:
          `position: relative` puts the paper in the flex row, where it occupies
          its own width and takes its height from the row, so the navigation ENDS
          where the row ends - above the shell's full-width bottom bar - and the
          content region simply takes the rest of the row (AppLayout, CHG-004).
          The docked root is the row's own flex item and stretches to the row's
          height, which is what the paper's `height: 100%` resolves against.
          The TEMPORARY drawer above is an overlay and is NOT changed. */}
      <Drawer
        variant="permanent"
        sx={{ display: { xs: 'none', sm: 'block' } }}
        // THE PAPER'S OWN sx, not a `& .MuiDrawer-paper` descendant selector on
        // the root. `position: relative` has to beat MUI's own
        // `position: fixed` on the paper (Drawer.js). A descendant selector on
        // the root wins that on SPECIFICITY, which a real browser applies and
        // jsdom does not: jsdom resolves the cascade in insertion order alone,
        // and the paper's base class is inserted when the paper renders, after
        // the root's - so `fixed` won there and the flow layout was provable
        // only in a browser we do not have. Through the slot the declaration
        // lands on the paper's OWN sx class, which is inserted after that base
        // class, so it wins under both resolution models.
        // `slotProps.paper` is the v6 API; `PaperProps` is deprecated.
        slotProps={{
          paper: {
            sx: {
              boxSizing: 'border-box',
              width: currentWidth,
              position: 'relative',
              height: '100%',
              borderRight: '1px solid',
              borderColor: 'divider',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          },
        }}
        open
      >
        {renderDrawerContent(isCollapsed)}
      </Drawer>
    </>
  );
};

export default Sidebar;
